// Palette + helper functions for the WAF event v3 UI consumer.
//
// All values come from the v3 envelope structured fields. Never
// invents data. When a field is null the helper returns a null/empty
// sentinel the consumer can render as "not available" — see
// docs/waf-event-schema-v3.md for the no-mock-data invariants this
// must respect.

import colors from "./colors";

// ── Severity ────────────────────────────────────────────────────────

export const SEVERITY_PALETTE = Object.freeze({
  critical: { fg: "#B91C1C", bg: "#FEE2E2", stripe: "#B91C1C", label: "Critical" },
  high:     { fg: "#C2410C", bg: "#FFEDD5", stripe: "#C2410C", label: "High" },
  medium:   { fg: "#A16207", bg: "#FEF3C7", stripe: "#CA8A04", label: "Medium" },
  low:      { fg: "#1F7C2A", bg: "#DCFCE7", stripe: "#16A34A", label: "Low" },
});

const NEUTRAL_PALETTE = Object.freeze({
  fg: colors.neutral[700], bg: colors.neutral[100], stripe: colors.neutral[400], label: "—",
});

// Resolve severity → palette. null/unknown → neutral palette (visually muted).
export function severityPalette(severity) {
  if (!severity) return NEUTRAL_PALETTE;
  return SEVERITY_PALETTE[String(severity).toLowerCase()] || NEUTRAL_PALETTE;
}

// ── Action chips ────────────────────────────────────────────────────

const ACTION_LABEL = Object.freeze({
  block:     "Blocked",
  challenge: "Challenged",
  detect:    "Detected",
  allow:     "Allowed",
  log_only:  "Logged",
  skipped:   "Skipped",
});

export function actionLabel(action) {
  if (!action) return "—";
  return ACTION_LABEL[String(action).toLowerCase()] || String(action);
}

// ── Intent icons (Lucide names — consumer imports the actual icon) ──

// Map intent_id → Lucide icon NAME. Components do their own import to
// stay compatible with the existing icon barrel.
export const INTENT_ICON_NAME = Object.freeze({
  INTENT_SSRF:                  "Network",
  INTENT_ACCESS_CONTROL:        "ShieldOff",
  INTENT_PATH_TRAVERSAL:        "FolderTree",
  INTENT_SQL_INJECTION:         "Database",
  INTENT_XSS:                   "Code",
  INTENT_COMMAND_INJECTION:     "Terminal",
  INTENT_XXE:                   "FileX",
  INTENT_LDAP_INJECTION:        "Users",
  INTENT_NOSQL_INJECTION:       "Database",
  INTENT_FILE_INCLUSION:        "FileWarning",
  INTENT_SSTI:                  "Braces",
  INTENT_INFORMATION_DISCLOSURE:"Eye",
  INTENT_OPEN_REDIRECT:         "ExternalLink",
});

export function intentIconName(intentId) {
  if (!intentId) return "Shield";
  return INTENT_ICON_NAME[intentId] || "Shield";
}

// ── Kill-chain stage icons + label ──────────────────────────────────

// observed semantics (CRITICAL UX rule):
//   true   → confirmed
//   false  → looked-and-did-not-find (only used for 'objective' stage today)
//   null   → not yet checked (visually distinct from false)
// See docs/waf-event-schema-v3.md.

export const KILL_CHAIN_STAGE_LABEL = Object.freeze({
  reconnaissance: "Reconnaissance",
  initial_access: "Initial Access",
  exploitation:   "Exploitation",
  objective:      "Objective",
});

export function killChainIconName(observed) {
  if (observed === true) return "Check";
  if (observed === false) return "X";
  return "Minus";        // null — not yet checked
}

export function killChainTone(observed) {
  if (observed === true)  return { fg: colors.green[300], bg: "rgba(31,193,107,0.12)" };
  if (observed === false) return { fg: colors.red[200], bg: colors.red[10] };
  return { fg: colors.neutral[600], bg: "#F3F4F6" };
}

// ── Burst badge ─────────────────────────────────────────────────────

// Format a burst count like "47×". Returns null when the badge
// should NOT render (count == 1 means the row is the only one in the
// window — no burst). Honest: caller renders nothing when this is
// null, not "1×" or "Only event".
export function formatBurstBadge(correlation) {
  if (!correlation) return null;
  const n = Number(correlation.related_event_count);
  if (!Number.isFinite(n) || n < 2) return null;
  return `${n}×`;
}

// Tooltip for the burst badge.
export function formatBurstTooltip(correlation) {
  if (!correlation) return null;
  const first = correlation.first_seen ? new Date(correlation.first_seen) : null;
  const last  = correlation.last_seen  ? new Date(correlation.last_seen)  : null;
  if (!first || !last) return null;
  return `${correlation.related_event_count} related events between ${first.toLocaleTimeString()} and ${last.toLocaleTimeString()}`;
}

// ── v3 envelope shape check ─────────────────────────────────────────

// Lightweight check the UI runs on every fetched event. Defensive
// against a misconfigured backend returning v2 when v3 was requested.
const REQUIRED_V3_KEYS = ["schema_version", "event_id", "assessment", "primary_intent", "attacker", "target", "intents", "correlation"];

export function isV3Envelope(ev) {
  if (!ev || typeof ev !== "object") return false;
  if (ev.schema_version !== "layerd.waf.event.v3") return false;
  for (const k of REQUIRED_V3_KEYS) {
    if (!(k in ev)) return false;
  }
  return true;
}

// ── Pretty-print a CWE / MITRE / OWASP chip ─────────────────────────

function formatClassificationChip(prefix, value) {
  const trimmed = String(value).trim();
  if (!trimmed) return null;
  const upperPrefix = prefix.toUpperCase();
  if (trimmed.toUpperCase().startsWith(`${upperPrefix}:`)) return trimmed;
  return `${prefix}: ${trimmed}`;
}

export function formatCweMitreOwasp(classification) {
  if (!classification) return [];
  const out = [];
  if (classification.cwe_id) {
    const label = formatClassificationChip("CWE", classification.cwe_id);
    if (label) out.push(label);
  }
  if (classification.mitre_id) {
    const label = formatClassificationChip("MITRE", classification.mitre_id);
    if (label) out.push(label);
  }
  if (classification.owasp_category) {
    const owaspId = classification.owasp_category.split(":")[0].trim();
    const label = formatClassificationChip("OWASP", owaspId);
    if (label) out.push(label);
  }
  return out;
}
